import * as React from 'react';

import IAnswer from '../../models/IAnswer';
import IAssessmentElement from '../../models/IAssessmentElement';
import AssessmentHelpers from '../../plugins/AssessmentHelpers';
import AssessmentLocalStorageManager from '../../plugins/AssessmentLocalStorageManager';

import { Actions, ActionType } from './actions';
import { store } from './store';
import { IAssessmentState, initialState } from './types';

const { Provider } = store;

function reducer(state = initialState, action: Actions): IAssessmentState 
{
	switch (action.type) 
	{
		case ActionType.SetAssessment:
		{
			const assessment = action.payload;
			return {
				...state,
				assessment: assessment,
				totalPages: assessment?.pages.length ?? 0
			}
		}
		case ActionType.SetPage:
			const page = action.payload;

			let index = -1;
			const pageAnswers = [] as IAnswer[]
			if (page && state.assessment) 
			{
				index = state.assessment.pages.indexOf(page) ?? -1;
				const allElements = AssessmentHelpers.getAllAssessmentQuestions(state.assessment);
				processPageSection(state, allElements, page.headerSection, pageAnswers);
				processPageSection(state, allElements, page.bodySection, pageAnswers);
				processPageSection(state, allElements, page.footerSection, pageAnswers);
			}

			AssessmentLocalStorageManager.setPageIndex(state.assessment?.assessmentId ?? '', index);

			return {
				...state,
				page: page,
				pageIndex: index,
				isFirstPage: index === 0,
				isLastPage: index + 1 === state.totalPages,
				pageAnswers: pageAnswers
			};
		case ActionType.SetAnswer:
		{
			const answer = action.payload;
			answer.value = AssessmentLocalStorageManager.setAnswerValues(state.assessment?.assessmentId ?? '', answer.id, answer.value);

			return {
				...state,
				pageAnswers: state.pageAnswers.map(x => x.id == answer.id ? answer : x)
			};
		}
		case ActionType.UpdateAssessmentElement:
		{
			if (!state.page)
				return state

			const element = action.payload;

			const page = {
				...state.page,
				headerSection: state.page.headerSection?.map(x => x.id == element.id ? element : x),
				bodySection: state.page.bodySection?.map(x => x.id == element.id ? element : x),
				footerSection: state.page.footerSection?.map(x => x.id == element.id ? element : x)
			};
			const allPageElements = AssessmentHelpers.getAllPageQuestions(page);
			const pageActionsAreComplete = allPageElements.filter(x => !x.actionsCompleted).length == 0

			return {
				...state,
				page: page,
				pageActionsComplete: pageActionsAreComplete
			};
		}
		default:
			return state;
	}
}


function processPageSection(state: IAssessmentState, allElements: IAssessmentElement[], sectionElements: IAssessmentElement[], pageAnswers: IAnswer[]) 
{
	for (let i = 0; i < sectionElements.length; i++) 
	{
		const question = sectionElements[i];

		sectionElements[i].actionsCompleted = true;

		const conditionalChilden = allElements.filter(q => q.showConditionSource == question.referenceId);
		if (conditionalChilden?.length > 0)
			sectionElements[i].hasConditionalChildren = true;

		const answerValues = AssessmentLocalStorageManager.getAnswerValues(state.assessment?.assessmentId ?? '', question.id);
		pageAnswers.push({
			id: question.id,
			value: answerValues
		});
	}
}

export function AssessmentContextProvider(props: React.PropsWithChildren) 
{
	const [state, dispatch] = React.useReducer(reducer, { ...initialState });

	return <Provider value={{ state, dispatch }}>{props.children}</Provider>;
}
